import { authService } from '../services/authService.js';
import { openAuthModal } from '../components/AuthModal.js';
import { Toast } from '../components/Toast.js';
import { seoService } from '../services/seoService.js';

export function renderAccountView() {
  seoService.update({
    title: 'Account & Member Profile',
    description: 'Manage your CinemaHala account, ratings, and content preferences.'
  });

  const container = document.createElement('div');
  container.className = 'main-content';

  const user = authService.getCurrentUser();

  if (!user) {
    container.innerHTML = `
      <div style="text-align: center; padding: var(--space-2xl) var(--space-md); max-width: 480px; margin: 0 auto;">
        <div style="font-size: 3.5rem; margin-bottom: var(--space-md);">👤</div>
        <h2 style="font-size: 1.5rem; font-weight: 800; margin-bottom: 8px;">CinemaHala Account</h2>
        <p style="color: var(--text-secondary); font-size: 0.95rem; margin-bottom: var(--space-xl); line-height: 1.5;">
          Sign in to save your 5-star movie ratings, track your watchlist, and join the Cinema Club discussions.
        </p>
        <button id="account-login-trigger" class="btn-primary clickable-item" style="padding: 12px 28px; font-size: 1rem;">
          Sign In or Register
        </button>
      </div>
    `;

    container.querySelector('#account-login-trigger').addEventListener('click', () => {
      openAuthModal(() => {
        window.location.hash = '#/account';
        window.location.reload();
      });
    });

    return container;
  }

  const isAdmin = authService.isAdmin();

  // Retrieve user ratings count
  let userRatingsCount = 0;
  try {
    const r = JSON.parse(localStorage.getItem('cmdb_user_ratings') || '{}');
    userRatingsCount = Object.keys(r).length;
  } catch (e) {
    userRatingsCount = 0;
  }

  // Retrieve watchlist count
  let watchlistCount = 0;
  try {
    const w = JSON.parse(localStorage.getItem('cmdb_watchlist') || '[]');
    watchlistCount = w.length;
  } catch (e) {
    watchlistCount = 0;
  }

  container.innerHTML = `
    <!-- User Profile Header -->
    <div style="background: var(--bg-surface); border: 1px solid var(--border-subtle); border-radius: var(--radius-lg); padding: var(--space-lg); margin-bottom: var(--space-xl); display: flex; gap: var(--space-md); align-items: center; flex-wrap: wrap;">
      <div style="width: 76px; height: 76px; border-radius: 50%; overflow: hidden; border: 2px solid var(--border-gold); flex-shrink: 0;">
        <img src="${user.avatar}" alt="${user.name}" style="width: 100%; height: 100%; object-fit: cover;" />
      </div>
      <div style="flex: 1; min-width: 220px;">
        <div style="display: flex; gap: 8px; align-items: center; margin-bottom: 4px;">
          <h2 style="font-size: 1.4rem; font-weight: 800;">${user.name}</h2>
          <span class="badge-pill ${isAdmin ? 'badge-gold' : 'badge-cyan'}">
            ${isAdmin ? '👑 Administrator' : '🎬 Member'}
          </span>
        </div>
        <p style="color: var(--text-muted); font-size: 0.85rem;">${user.email}</p>
      </div>
      <button id="logout-btn" class="btn-secondary clickable-item" style="padding: 8px 14px; font-size: 0.82rem;">
        Sign Out
      </button>
    </div>

    <!-- Admin Section -->
    ${isAdmin ? `
      <section style="background: var(--bg-surface-elevated); border: 1px solid var(--border-gold); border-radius: var(--radius-lg); padding: var(--space-lg); margin-bottom: var(--space-xl);">
        <div class="section-header">
          <div>
            <h3 class="section-title">Administrator Tools</h3>
            <p style="color: var(--text-muted); font-size: 0.82rem; margin-top: 2px;">Database and content management operations</p>
          </div>
        </div>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: var(--space-md); margin-top: var(--space-md);">
          <a href="#/admin" class="btn-primary clickable-item" style="padding: 14px; display: flex; align-items: center; justify-content: center; gap: 8px;">
            <span>➕</span>
            <span>Add Movie Data & CMS</span>
          </a>
          <div style="background: var(--bg-surface); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 12px 16px;">
            <div style="font-size: 0.75rem; color: var(--text-muted); text-transform: uppercase; font-weight: 700;">Database Connection</div>
            <div style="color: var(--accent-emerald); font-weight: 700; font-size: 0.95rem; margin-top: 2px; display: flex; align-items: center; gap: 6px;">
              <span style="width: 8px; height: 8px; border-radius: 50%; background: var(--accent-emerald); display: inline-block;"></span>
              Cloudflare D1: cinemahala-db
            </div>
          </div>
        </div>
      </section>
    ` : ''}

    <!-- General Member Activity Dashboard -->
    <section style="margin-bottom: var(--space-xl);">
      <div class="section-header">
        <h3 class="section-title">Your Cinema Activity</h3>
      </div>

      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: var(--space-md); margin-top: var(--space-md);">
        <div style="background: var(--bg-surface); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: var(--space-md);">
          <div style="font-size: 0.8rem; color: var(--text-muted);">Rated Titles</div>
          <div style="font-size: 1.8rem; font-weight: 800; color: var(--accent-gold); margin: 4px 0;">${userRatingsCount}</div>
          <p style="font-size: 0.78rem; color: var(--text-secondary);">Movies rated using the 5-star rating system</p>
        </div>

        <a href="#/watchlist" class="clickable-item" style="background: var(--bg-surface); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: var(--space-md); display: block;">
          <div style="font-size: 0.8rem; color: var(--text-muted);">Saved Watchlist</div>
          <div style="font-size: 1.8rem; font-weight: 800; color: var(--accent-cyan); margin: 4px 0;">${watchlistCount}</div>
          <p style="font-size: 0.78rem; color: var(--text-secondary);">Tap to view your saved titles →</p>
        </a>

        <div style="background: var(--bg-surface); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: var(--space-md);">
          <div style="font-size: 0.8rem; color: var(--text-muted);">Community Status</div>
          <div style="font-size: 1.1rem; font-weight: 700; color: var(--text-primary); margin: 8px 0;">Verified Cinephile</div>
          <p style="font-size: 0.78rem; color: var(--text-secondary);">Cinema Club discussion privileges active</p>
        </div>
      </div>
    </section>
  `;

  container.querySelector('#logout-btn').addEventListener('click', () => {
    authService.logout();
    Toast.show('Signed out of CinemaHala');
    window.location.hash = '#/';
    window.location.reload();
  });

  return container;
}
